import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { BarChart3, Calendar, Heart, Menu, Moon, Sun, X } from "lucide-react";
import Button from "./ui/Button";
import BrandMark from "./BrandMark";
import SiteBrowseLinks from "./SiteBrowseLinks";
import useScrollPosition from "../hooks/useScrollPosition";
import { cn } from "../utils/classNames";
import { useTheme } from "../contexts/ThemeContext";
import { handleInternalLinkClick } from "../utils/navigation";

const NewHeader = React.forwardRef(
  (
    {
      className = "",
      insightsSummary = null,
      onInsightsToggle = () => {},
      insightsOpen = false,
      watchlistCount = 0,
      ...props
    },
    ref
  ) => {
    const isScrolled = useScrollPosition(120);
    const { isDarkMode, toggleDarkMode } = useTheme();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuButtonRef = useRef(null);

    const summaryCount = insightsSummary?.count ?? 0;
    const summaryRelative = insightsSummary?.relativeUpdated;
    const percentageNew = insightsSummary?.newVsReturning?.percentageNew ?? 0;
    const percentageNewLabel = `${Math.round(percentageNew * 100)}% new`;
    const summarySubtitle = summaryRelative
      ? `${summaryRelative} • ${percentageNewLabel}`
      : percentageNew > 0
        ? percentageNewLabel
        : "Stay updated";
    const summaryTitle = `${summaryCount} ${summaryCount === 1 ? "show" : "shows"}`;

    const [nowMs, setNowMs] = useState(0);

    useEffect(() => {
      const rafId = window.requestAnimationFrame(() => setNowMs(Date.now()));
      const intervalId = window.setInterval(() => {
        setNowMs(Date.now());
      }, 60000);
      return () => {
        window.cancelAnimationFrame(rafId);
        window.clearInterval(intervalId);
      };
    }, []);

    useEffect(() => {
      if (!menuOpen) return undefined;
      const handleKeyDown = (event) => {
        if (event.key === "Escape") {
          setMenuOpen(false);
          menuButtonRef.current?.focus();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [menuOpen]);

    const updatedAt = insightsSummary?.lastUpdated ? new Date(insightsSummary.lastUpdated) : null;
    const isFresh = updatedAt ? nowMs - updatedAt.getTime() < 1000 * 60 * 60 * 6 : false;

    return (
      <header
        ref={ref}
        className={cn(
          "fixed inset-x-0 top-0 z-50 border-b border-accent-gold/35 transition-colors shadow-header",
          isScrolled ? "backdrop-blur supports-[backdrop-filter]:bg-background/80" : "bg-background",
          className
        )}
        {...props}
      >
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-10 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-body-small focus:font-semibold focus:text-primary-foreground"
        >
          Skip to content
        </a>
        {/* Film strip top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-film-strip" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 sm:h-20 min-h-[4rem] sm:min-h-[5rem] items-center gap-4">
            {/* Brand */}
            <BrandMark />

            {/* Header actions */}
            <div className="relative ml-auto flex items-center gap-2">
              <nav className="hidden xl:flex items-center gap-2" aria-label="Browse TV"><SiteBrowseLinks /></nav>
              <Button asChild variant="ghost" className="subpage-header__action">
                <a
                  href="/my-list"
                  onClick={(event) => handleInternalLinkClick(event, "/my-list")}
                  aria-label={`Open My List${watchlistCount ? `, ${watchlistCount} saved shows` : ""}`}
                >
                  <div className="subpage-header__action-icon">
                    <Heart className="h-5 w-5" />
                    {watchlistCount > 0 && (
                      <span className="absolute -right-1 -top-1 flex min-w-5 items-center justify-center border border-background bg-secondary px-1 py-0.5 text-micro font-bold leading-none text-secondary-foreground">
                        {watchlistCount > 99 ? "99+" : watchlistCount}
                      </span>
                    )}
                  </div>
                  <span className="subpage-header__action-label">My List</span>
                </a>
              </Button>
              <Button asChild variant="ghost" className="subpage-header__action hidden xl:flex">
                <a
                  href="/archive"
                  onClick={(event) => handleInternalLinkClick(event, "/archive")}
                >
                  <div className="subpage-header__action-icon">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <span className="subpage-header__action-label">Archive</span>
                </a>
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={onInsightsToggle}
                aria-pressed={insightsOpen}
                aria-label={`Toggle upcoming overview (${summaryTitle}${summarySubtitle ? `, ${summarySubtitle}` : ""})`}
                className="subpage-header__action hidden xl:flex"
              >
                <div className="subpage-header__action-icon">
                  <BarChart3 className="h-5 w-5" />
                  {isFresh && <span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-secondary" />}
                </div>
                <span className="subpage-header__action-label">Overview</span>
              </Button>
              <Button
                ref={menuButtonRef}
                type="button"
                variant="ghost"
                onClick={() => setMenuOpen((current) => !current)}
                aria-expanded={menuOpen}
                aria-controls="compact-site-navigation"
                className="subpage-header__action xl:hidden"
              >
                <div className="subpage-header__action-icon">
                  {menuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
                </div>
                <span className="subpage-header__action-label">Menu</span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                className="subpage-header__theme"
              >
                {isDarkMode ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>

              {menuOpen && (
                <nav
                  id="compact-site-navigation"
                  aria-label="Site navigation"
                  className="absolute right-0 top-[calc(100%+0.75rem)] z-50 w-64 rounded-lg border border-accent-gold/60 bg-card p-2 shadow-lg xl:hidden"
                >
                  <SiteBrowseLinks onNavigate={() => setMenuOpen(false)} />
                  <a
                    href="/archive"
                    className="flex min-h-11 w-full items-center gap-3 rounded-md px-3 text-left text-body-small font-semibold text-foreground hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={(event) => {
                      setMenuOpen(false);
                      handleInternalLinkClick(event, "/archive");
                    }}
                  >
                    <Calendar className="h-5 w-5 text-primary" aria-hidden="true" />
                    Archive
                  </a>
                  <button
                    type="button"
                    className="flex min-h-11 w-full items-center gap-3 rounded-md px-3 text-left text-body-small font-semibold text-foreground hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => {
                      // This item unmounts as the panel opens, so the panel returns focus to Menu.
                      menuButtonRef.current?.focus();
                      setMenuOpen(false);
                      onInsightsToggle();
                    }}
                  >
                    <BarChart3 className="h-5 w-5 text-primary" aria-hidden="true" />
                    Upcoming overview
                    {isFresh ? <span className="ml-auto h-2.5 w-2.5 bg-secondary" aria-hidden="true" /> : null}
                  </button>
                </nav>
              )}
            </div>
          </div>
        </div>
      </header>
    );
  }
);

NewHeader.displayName = "NewHeader";

NewHeader.propTypes = {
  className: PropTypes.string,
  insightsSummary: PropTypes.shape({
    count: PropTypes.number,
    relativeUpdated: PropTypes.string,
    newVsReturning: PropTypes.shape({ percentageNew: PropTypes.number }),
    trend: PropTypes.shape({ delta: PropTypes.number }),
    lastUpdated: PropTypes.string,
  }),
  onInsightsToggle: PropTypes.func,
  insightsOpen: PropTypes.bool,
  watchlistCount: PropTypes.number,
};

export default NewHeader;
